import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAutoRefresh — calls `fetchFn` on:
 *   • mount (skippable via { skipInitial: true })
 *   • window focus / tab visibility return (always, with a small dedupe)
 *   • optional polling interval when `polling` is true
 *
 * Returns { refresh, refreshing, lastUpdatedAt, livePolling, setLivePolling }.
 */
export default function useAutoRefresh(fetchFn, {
  intervalMs = 30000,
  initialPolling = false,
  skipInitial = false,
  focusDedupeMs = 2000,
} = {}) {
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [livePolling, setLivePolling] = useState(initialPolling);
  const inFlight = useRef(false);
  const lastRunAt = useRef(0);
  const fnRef = useRef(fetchFn);
  useEffect(() => { fnRef.current = fetchFn; }, [fetchFn]);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (!silent) setRefreshing(true);
    try {
      await fnRef.current?.();
      setLastUpdatedAt(new Date());
      lastRunAt.current = Date.now();
    } finally {
      inFlight.current = false;
      if (!silent) setRefreshing(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (skipInitial) return;
    refresh({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Window focus / visibility
  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastRunAt.current < focusDedupeMs) return;
      refresh({ silent: true });
    };
    const onVis = () => { if (!document.hidden) onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refresh, focusDedupeMs]);

  // Polling
  useEffect(() => {
    if (!livePolling) return;
    const id = setInterval(() => refresh({ silent: true }), intervalMs);
    return () => clearInterval(id);
  }, [livePolling, intervalMs, refresh]);

  return { refresh, refreshing, lastUpdatedAt, livePolling, setLivePolling };
}
